import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import IcdSearchService from "./IcdSearchService";

/**
 * Debounced ICD-10 fuzzy-search dropdown for the complaint editor.
 *
 * ICD server response shape (verified with the live endpoint):
 *   { label, value, code, managedRequired, onMedicationRequired }
 *     - label = "A01.4 Paratyphoid fever, unspecified"  (display + code prefix)
 *     - value = "ICD17"                                 (Zyla internal id)
 *     - code  = "A01.4"                                 (ICD-10 code — dedup key)
 *
 * Display uses `label`; row-uniqueness uses `code`. When the user picks a
 * suggestion we strip the code prefix so the saved complaint reads
 * "Paratyphoid fever, unspecified" not "A01.4 Paratyphoid fever, unspecified".
 */
const DEBOUNCE_MS = 250;

const stripCodePrefix = (label, code) => {
  if (!label || !code) return label || "";
  const prefix = code + " ";
  if (label.startsWith(prefix)) return label.slice(prefix.length);
  return label;
};

// Dedupe by `code` (ICD-10). Keeps the first occurrence, drops later
// duplicates — the server usually only returns one row per code but the
// contract doesn't guarantee it.
const dedupeByCode = (rows) => {
  const seen = new Set();
  const out = [];
  for (const r of rows || []) {
    const key = (r && r.code) || (r && r.value) || "";
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
};

const IcdAutocomplete = ({
  value,
  code,
  onChange,
  disabled,
  isAlreadyActive,
  alreadyActiveTitle,
  placeholder,
  aiSuggestion,
}) => {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);
  const serviceRef = useRef(null);
  // Monotonic request token. Bumped on every keystroke; each search
  // callback captures the token at request time and only applies the
  // response if it still matches the latest. Guards against the classic
  // typo-then-pause-then-keep-typing race where an earlier request's
  // slow response arrives AFTER the current one and clobbers it.
  const seqRef = useRef(0);

  // Frozen snapshot of the AI's original suggestion at mount time. The
  // pill renders straight from this — it stays visible for the life of
  // the sheet even after the reviewer picks something else (that's the
  // "modified" state). Immutable across renders.
  const aiRef = useRef(aiSuggestion || null);

  if (!serviceRef.current) serviceRef.current = new IcdSearchService();

  // Sync down when the parent resets the field (e.g. after a successful save).
  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  // Code-based resolve on mount for transcript-mode AI pre-seed. When
  // the form opens with BOTH a complaint text AND an ICD code, look the
  // code up against the live ICD service and treat the exact match the
  // same way a user pick would — canonical display + canonical code
  // flow through onChange, so the parent's state ends up looking like
  // a standalone-picked entry (input reads the canonical name, code
  // binding is verified, save button lights up).
  //
  // Three cases:
  //   * Search succeeds + exact match on r.code: canonicalise via
  //     onChange (same shape pick() emits). Any drift between the
  //     LLM's icd_description and Zyla's ICD data is silently
  //     corrected.
  //   * Search succeeds + NO exact match: the LLM's ICD isn't in our
  //     service. Clear complaintCode via onChange so the save button
  //     disables and the reviewer has to search + pick themselves.
  //     Complaint text stays as the LLM's guess so it seeds their
  //     next search.
  //   * Search errors (network): leave the binding as-is. LLM's ICD
  //     was already validated Python-side; a transient net error
  //     shouldn't drop it.
  //
  // Fires ONCE per mount; a later external `value` change (e.g. the
  // sync effect above after a save) doesn't re-trigger.
  const didInitialResolve = useRef(false);
  useEffect(() => {
    if (didInitialResolve.current) return;
    const seedText = (value || "").trim();
    if (!seedText) return;
    didInitialResolve.current = true;
    // Two paths depending on what the caller handed us:
    //   1. value + code   → search by code, auto-pick the exact match
    //                        so the field lands with a verified ICD.
    //   2. value only     → search by the text, auto-pick when the
    //                        first row's name is an exact case-
    //                        insensitive match (mirrors Medicine's
    //                        exact-match auto-pick). If no exact hit,
    //                        stash results anyway so the reviewer's
    //                        first click on the field opens the
    //                        dropdown with hits already loaded.
    const query = code ? code : seedText;
    serviceRef.current.search(
      query,
      (data) => {
        if (!mountedRef.current) return;
        const rows = dedupeByCode(Array.isArray(data) ? data : []);
        setResults(rows);
        let hit = null;
        if (code) {
          hit = rows.find(
            (r) => (r.code || "").toUpperCase() === code.toUpperCase()
          );
        } else {
          const seedLower = seedText.toLowerCase();
          hit = rows.find((r) => {
            const label = stripCodePrefix(r.label || "", r.code || "");
            return label.trim().toLowerCase() === seedLower;
          });
        }
        if (hit) {
          const display = stripCodePrefix(hit.label, hit.code);
          onChange({
            complaint: display,
            complaintCode: {
              code:    hit.value || "",
              label:   display,
              icdCode: hit.code || "",
            },
          });
        } else if (code) {
          // We had a code but couldn't verify it — clear the binding so
          // the reviewer can pick a real one instead of saving under a
          // ghost ICD. The text stays visible in the field.
          onChange({ complaint: value, complaintCode: null });
        }
        // Value-only path with no exact hit: leave complaintCode
        // untouched (parent already carries it as null), keep the
        // typed value, and let the stashed results surface on focus.
      },
      () => {
        // Network error — do not touch complaintCode. Leave the
        // LLM's binding intact so a transient outage doesn't cost the
        // reviewer their AI-suggested code.
      }
    );
    // Fires once per mount only; deps left empty on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The 250ms debounce and its in-flight search both outlive an unmount:
  // every stacked sheet has its own Cancel and every successful save drops a
  // sheet, so typing-then-closing is now a routine path rather than a rare
  // one. Without this the timer fires into a dead component and React warns.
  const mountedRef = useRef(true);
  useEffect(() => () => {
    mountedRef.current = false;
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  // Close on outside click or Escape. Listeners only run while the
  // dropdown is open so the picker doesn't hold a document listener the
  // rest of its life.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Shared search primitive. `immediate` skips the 250ms debounce
  // (used by AI-pill click paths where the reviewer explicitly asked
  // for that query). Every call bumps the seq token so a still-in-
  // flight prior request from a stale keystroke can't race in and
  // clobber the newer results.
  const runSearch = (q, { immediate = false } = {}) => {
    const mySeq = ++seqRef.current;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) {
      setResults([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    const fire = () => {
      if (!mountedRef.current) return;
      if (mySeq !== seqRef.current) return;
      serviceRef.current.search(
        q,
        (data) => {
          if (!mountedRef.current) return;
          if (mySeq !== seqRef.current) return;
          setResults(dedupeByCode(Array.isArray(data) ? data : []));
          setOpen(true);
          setLoading(false);
        },
        () => {
          if (!mountedRef.current) return;
          if (mySeq !== seqRef.current) return;
          setResults([]);
          setLoading(false);
        }
      );
    };
    if (immediate) fire();
    else debounceRef.current = setTimeout(fire, DEBOUNCE_MS);
  };

  const handleType = (e) => {
    const q = e.target.value;
    setQuery(q);
    // Free-text edit clears the ICD code — user must re-pick to relink.
    onChange({ complaint: q, complaintCode: null });
    runSearch(q);
  };

  // AI-pill click: put the clicked value into the input, clear
  // complaintCode (this is a fresh search, not a confirmation of the
  // current pick), fire an immediate search, and open the dropdown.
  // Same shape as if the reviewer typed the AI's code/text themselves.
  const runAiPillSearch = (q) => {
    if (!q) return;
    setQuery(q);
    onChange({ complaint: q, complaintCode: null });
    runSearch(q, { immediate: true });
  };

  const pick = (item) => {
    const display = stripCodePrefix(item.label, item.code);
    // Deliberately DO NOT set the local `query` here. The parent's
    // onChange controls what the input should display next by updating
    // `value`, and the sync effect above (`useEffect on [value]`) will
    // mirror it into local `query`.
    //
    // Why: multi-pick callers (Family history) clear their state after
    // a pick — parent's onChange calls setValue(""). When `value` was
    // already "" (reviewer clicked an item from the curated initial
    // list without typing), React bails on the same-string setState, so
    // the sync effect doesn't fire. Pre-setting local `query = display`
    // here would leave the input stuck showing the picked name after
    // the parent cleared its state. Not pre-setting it fixes that
    // without breaking single-pick callers (CC / Dx / Hospitalisation)
    // — they always set `value = display`, which transitions the prop
    // and triggers the sync effect anyway.
    setOpen(false);
    onChange({
      complaint: display,
      complaintCode: {
        code: item.value || "",  // Zyla internal id → CodeValuePair.Code
        label: display,          // stripped display                → CodeValuePair.Label
        icdCode: item.code || "", // ICD-10 (M54.5 / A01.4 / …)     → CodeValuePair.ICDCode (dedup key)
      },
    });
  };

  // Modified state — the reviewer picked something OTHER than what the
  // AI suggested (either a different ICD or cleared it entirely). Only
  // meaningful when an AI suggestion exists in the first place.
  const isModifiedFromAi = !!aiRef.current
    && ((code || "").toUpperCase() !== (aiRef.current.icdCode || "").toUpperCase());

  return (
    <div className="cc-icd" ref={boxRef}>
      {aiRef.current ? (
        <div
          className={`cc-icd-ai-pill${isModifiedFromAi ? " is-modified" : ""}`}
          role="group"
          aria-label="AI suggested ICD"
        >
          <span className="cc-icd-ai-pill-brand" aria-hidden="true">
            {/* Stroke-based sparkle matching the product mockup — a
                four-point compass star, drawn with a 1.4 px white
                stroke and round joins/caps against the burgundy chip
                so the shape reads clearly at 12 px. */}
            <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path
                d="M6 1 L7 4 L10 5 L7 6 L6 9 L5 6 L2 5 L5 4 Z"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            AI
          </span>
          {aiRef.current.icdCode ? (
            <button
              type="button"
              className="cc-icd-ai-pill-code"
              onClick={() => runAiPillSearch(aiRef.current.icdCode)}
              disabled={disabled}
              title="Search this ICD code"
            >
              {aiRef.current.icdCode}
            </button>
          ) : null}
          {aiRef.current.extractedText ? (
            <button
              type="button"
              className="cc-icd-ai-pill-text"
              onClick={() => runAiPillSearch(aiRef.current.extractedText)}
              disabled={disabled}
              title="Search this text"
            >
              {aiRef.current.extractedText}
            </button>
          ) : null}
          {isModifiedFromAi ? (
            <span className="cc-icd-ai-pill-mod" aria-label="Modified">
              Modified
            </span>
          ) : null}
        </div>
      ) : null}
      <div className="sec-search">
        <svg
          className="sec-search-icon"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          aria-hidden="true"
        >
          <circle cx="9" cy="9" r="6" />
          <path d="M14 14l4 4" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          value={query}
          onChange={handleType}
          onFocus={() => query && results.length > 0 && setOpen(true)}
          placeholder={placeholder || "Search a complaint"}
          disabled={disabled}
        />
      </div>
      {/* Dropdown per "Search results overlay (Diagnosis)" (2961:5051):
          same sheet as the lab dropdown, each row carrying its ICD tag pill. */}
      {open && (
        <ul className="li3-dd" role="listbox">
          {loading && <li className="li3-dd-empty">Searching…</li>}
          {!loading && results.length === 0 && (
            <li className="li3-dd-empty">
              No matches — refine the search to pick from the list.
            </li>
          )}
          {!loading &&
            results.map((r) => {
              // Upstream labels ship as "A04 Other bacterial…" — the leading
              // code duplicates the ICD chip on the right. Strip it here so
              // the row reads as `<name>            [ICD A04]` on every
              // caller (Dx / CC / Hospitalisation) at once. If the label
              // doesn't start with the code we leave it untouched.
              const rawLabel = (r.label || r.value || "").toString();
              const chipCode = (r.icdCode || r.code || "").toString();
              const displayLabel = chipCode && rawLabel.startsWith(chipCode)
                ? rawLabel.slice(chipCode.length).replace(/^[\s.:\-–—]+/, "")
                : rawLabel;
              const alreadyActive = isAlreadyActive ? !!isAlreadyActive(r) : false;
              const isSelected = code && r.code === code;
              return (
                <li
                  key={r.code || r.value}
                  role="option"
                  aria-selected={!!isSelected}
                  aria-disabled={alreadyActive}
                  className={`li3-dd-opt icd-opt${
                    isSelected ? " is-active" : ""
                  }${alreadyActive ? " is-disabled" : ""}`}
                  onClick={() => {
                    // Silent no-op — the greyed row + pill tell the reviewer
                    // why. Update the row from the section table instead.
                    if (alreadyActive) return;
                    pick(r);
                  }}
                  title={
                    alreadyActive
                      ? (alreadyActiveTitle || "Already in the list — update it from the row menu")
                      : undefined
                  }
                >
                  <span className="icd-opt-name">{displayLabel}</span>
                  {/* Right-side stack: ICD chip on top, state pill
                      (already added) directly beneath. Same layout the
                      medicine / allergen / activity pickers use. */}
                  <span className="cc-icd-side">
                    {chipCode && (
                      <span className="icd-tag">
                        <span className="icd-tag-dot" aria-hidden="true" />
                        ICD {chipCode}
                      </span>
                    )}
                    {alreadyActive && (
                      <span className="cc-icd-active-tag" aria-hidden="true">
                        already added
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
};

IcdAutocomplete.propTypes = {
  value: PropTypes.string,
  code: PropTypes.string, // currently-picked ICD-10 code, if any
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  /** Predicate (row → bool). When true the option renders greyed with an
      "already added" pill and clicks silently no-op. Callers pass the
      predicate; CC / Hospitalisation currently pass no prop, so the
      behavior is opt-in. */
  isAlreadyActive: PropTypes.func,
  /** Section-specific hover tooltip on already-added rows. Falls back to
      a generic "Already in the list…" when unset. */
  alreadyActiveTitle: PropTypes.string,
  /** Section-specific input placeholder. Defaults to "Search a complaint"
      (this component originated in Chief complaints) — Diagnosis /
      Hospitalisation / Family history override to name their own
      entity. */
  placeholder: PropTypes.string,
  /** AI-suggested ICD from a transcript-captured payload. When set, an
   *  always-visible pill renders above the search input with two
   *  clickable buttons — the ICD code and the LLM's extracted text.
   *  Either click re-runs a search for that value + opens the
   *  dropdown so the reviewer can confirm/pick. A "Modified" tag
   *  appears when the current pick differs from the AI's suggestion.
   *  Frozen at mount — later changes to the prop are ignored. */
  aiSuggestion: PropTypes.shape({
    icdCode:       PropTypes.string,
    extractedText: PropTypes.string,
  }),
};

export default IcdAutocomplete;
